import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Editor from "@monaco-editor/react";
import api from "../api";
import { ChevronLeft, RotateCcw, Send, Variable, Smartphone, Monitor, Check } from "lucide-react";

const VARS = [
  { key: "SALUTO", desc: "Es. 'Gentile Dott. Mario Rossi' (auto da titolare se disponibile)" },
  { key: "TITOLARE", desc: "Nome+cognome del legale rappresentante" },
  { key: "NOME_TITOLARE", desc: "Solo nome (es. 'Mario')" },
  { key: "COGNOME_TITOLARE", desc: "Solo cognome (es. 'Rossi')" },
  { key: "RUOLO_TITOLARE", desc: "Ruolo aziendale del titolare" },
  { key: "DOMINIO", desc: "Es. 'esempio.it' (senza protocollo)" },
  { key: "companyName", desc: "Ragione sociale completa" },
  { key: "CITTA", desc: "Città azienda" },
  { key: "PAGESPEED_MOBILE", desc: "Score Lighthouse mobile (0-100)" },
  { key: "PAGESPEED_DESKTOP", desc: "Score Lighthouse desktop (0-100)" },
  { key: "SEOZOOM_AUTHORITY", desc: "Z-Authority del dominio" },
  { key: "SEOZOOM_TRAFFIC", desc: "Traffico stimato/mese (formattato)" },
  { key: "TOTAL_KEYWORDS", desc: "Numero keyword posizionate" },
  { key: "analysisUrl", desc: "URL pagina pubblica del report" },
  { key: "calendarUrl", desc: "URL prenotazione consulenza" },
  { key: "bookUrl", desc: "URL download libro" },
];

const SAMPLE = {
  SALUTO: "Gentile Dott. Mario Rossi",
  TITOLARE: "Mario Rossi",
  NOME_TITOLARE: "Mario",
  COGNOME_TITOLARE: "Rossi",
  RUOLO_TITOLARE: "Amministratore Unico",
  DOMINIO: "studiorossi.it",
  companyName: "Studio Dentistico Rossi SRL",
  CITTA: "Milano",
  PAGESPEED_MOBILE: "32",
  PAGESPEED_DESKTOP: "58",
  SEOZOOM_AUTHORITY: "12",
  SEOZOOM_TRAFFIC: "1.250",
  TOTAL_KEYWORDS: "47",
  analysisUrl: "https://lead.maxvalle.app/analisi/demotoken",
  calendarUrl: "https://calendar.app.google/F1Qg6SoTkmh9dn7D7",
  bookUrl: "https://drive.google.com/file/d/1dH8kIy4MBsoXELbrcXLe-o-gl1U5CBzq/view",
};

const renderVars = (html, ctx) => {
  let out = html || "";
  for (const [k, v] of Object.entries(ctx)) out = out.split(`{{${k}}}`).join(v);
  return out;
};

export default function EmailTemplate() {
  const { id } = useParams();
  const [list, setList] = useState(null);
  const [seq, setSeq] = useState(1); // 1 | 2 | 3
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewMobile, setViewMobile] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showVars, setShowVars] = useState(false);
  const timerRef = useRef();

  // Field name helpers based on the active sequence
  const subjKey = seq === 1 ? "emailSubjectTemplate" : `emailSubjectTemplate${seq}`;
  const bodyKey = seq === 1 ? "emailBodyHtml" : `emailBodyHtml${seq}`;

  useEffect(() => {
    api.get(`/lists/${id}`).then(r => {
      setList(r.data);
    });
  }, [id]);

  // Whenever the list loads or the user switches tab, refresh the editor
  useEffect(() => {
    if (!list) return;
    setSubject(list[subjKey] || "");
    setHtml(list[bodyKey] || "");
    setSavedAt(null);
  }, [list, seq]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (s, h) => {
    setSaving(true);
    try {
      await api.put(`/lists/${id}`, { [subjKey]: s, [bodyKey]: h });
      setSavedAt(new Date());
      // Update local cache so the tab-switch effect doesn't blow away unsaved edits
      setList(prev => prev ? { ...prev, [subjKey]: s, [bodyKey]: h } : prev);
    } finally { setSaving(false); }
  };

  // Debounced autosave
  useEffect(() => {
    if (!list) return;
    if (subject === (list[subjKey] || "") && html === (list[bodyKey] || "")) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(subject, html), 1500);
    return () => clearTimeout(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, html, list?.id, seq]);

  const reset = async () => {
    if (seq !== 1) {
      if (!window.confirm("Svuotare il template T" + seq + "?")) return;
      setSubject(""); setHtml("");
      return;
    }
    if (!window.confirm("Ripristinare il template T1 di default? Le modifiche verranno perse.")) return;
    const { data } = await api.post(`/lists/${id}/reset-template`);
    setSubject(data.emailSubjectTemplate); setHtml(data.emailBodyHtml); setList(data);
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/email/test`, { subject, html });
      if (data.success) alert("Email di test inviata!");
      else alert("Errore: " + (data.error || "sconosciuto"));
    } finally { setTesting(false); }
  };

  const insertVar = (v) => setHtml(h => h + `{{${v}}}`);

  if (!list) return <div><span className="spinner"/></div>;

  const previewHtml = renderVars(html, SAMPLE);
  const previewSubject = renderVars(subject, SAMPLE);

  const TAB_INFO = [
    { seq: 1, label: "T1 · Primo invio", desc: "Email iniziale, inviata manualmente o in bulk" },
    { seq: 2, label: "T2 · Reminder", desc: "Inviato automaticamente dopo 5 giorni (configurabile)" },
    { seq: 3, label: "T3 · Ultimo tentativo", desc: "Inviato automaticamente dopo 15 giorni" },
  ];
  const currentTab = TAB_INFO.find(t => t.seq === seq);

  return (
    <div data-testid="email-template-page" style={{height:"calc(100vh - 64px)",display:"flex",flexDirection:"column"}}>
      <Link to={`/liste/${id}`} style={{display:"inline-flex",alignItems:"center",gap:6,color:"var(--mv-text-muted)",marginBottom:8}}><ChevronLeft size={14}/> Torna a {list.name}</Link>

      <div style={{display:"flex", gap:0, marginBottom:14, borderBottom:"2px solid var(--mv-border)"}}>
        {TAB_INFO.map(t => {
          const isActive = t.seq === seq;
          const hasContent = !!(list[t.seq === 1 ? "emailSubjectTemplate" : `emailSubjectTemplate${t.seq}`]
                              && list[t.seq === 1 ? "emailBodyHtml" : `emailBodyHtml${t.seq}`]);
          return (
            <button key={t.seq}
              data-testid={`tab-T${t.seq}`}
              onClick={() => setSeq(t.seq)}
              style={{
                padding:"10px 18px",
                background: isActive ? "var(--mv-primary)" : "transparent",
                color: isActive ? "#fff" : "var(--mv-text)",
                border: "none",
                borderTopLeftRadius:6, borderTopRightRadius:6,
                fontWeight: 600, cursor:"pointer",
                marginBottom:-2,
                borderBottom: isActive ? "2px solid var(--mv-primary)" : "2px solid transparent",
                fontSize:14,
              }}>
              {t.label}
              {!hasContent && t.seq > 1 && (
                <span style={{
                  marginLeft:8, fontSize:10, padding:"1px 6px",
                  background: isActive ? "rgba(255,255,255,0.2)" : "var(--mv-bg-soft)",
                  borderRadius:8, color: isActive ? "#fff" : "var(--mv-text-muted)",
                }}>vuoto</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex justify-between items-center mb-3" style={{flexWrap:"wrap",gap:8}}>
        <div>
          <div className="h-section">Template {currentTab.label} — {list.name}</div>
          <div className="text-small text-muted">
            {currentTab.desc} · {saving ? "Salvataggio…" : savedAt ? (<><Check size={12} style={{display:"inline",verticalAlign:"middle"}}/> Salvato {savedAt.toLocaleTimeString("it-IT")}</>) : "Pronto"}
          </div>
        </div>
        <div className="flex gap-2" style={{flexWrap:"wrap"}}>
          <button onClick={()=>setShowVars(true)} className="btn btn-secondary btn-sm" data-testid="vars-btn"><Variable size={14}/> Variabili</button>
          <button onClick={reset} className="btn btn-secondary btn-sm" data-testid="reset-template-btn"><RotateCcw size={14}/> {seq === 1 ? "Reset T1" : `Svuota T${seq}`}</button>
          <button onClick={sendTest} disabled={testing} className="btn btn-primary btn-sm" data-testid="send-test-btn">
            {testing ? <span className="spinner"/> : <><Send size={14}/> Invia test a me</>}
          </button>
        </div>
      </div>

      <div className="card mb-3" style={{padding:14}}>
        <label className="label">Oggetto</label>
        <input className="input" value={subject} onChange={e=>setSubject(e.target.value)} data-testid="email-subject-input" />
        <div className="text-small text-muted mt-2">Preview: <strong>{previewSubject}</strong></div>
      </div>

      <div className="grid gap-3" style={{gridTemplateColumns:"1fr 1fr",flex:1,minHeight:0}}>
        <div className="card" style={{padding:0,overflow:"hidden",display:"flex",flexDirection:"column"}}>
          <div style={{padding:"10px 14px",borderBottom:"1px solid var(--mv-border)",fontSize:13,fontWeight:600}}>HTML</div>
          <div style={{flex:1,minHeight:0}} data-testid="monaco-editor">
            <Editor
              height="100%" language="html" theme="vs-light" value={html}
              onChange={(v)=>setHtml(v || "")}
              options={{ minimap:{enabled:false}, fontSize:13, wordWrap:"on", lineNumbers:"on", scrollBeyondLastLine:false }}
            />
          </div>
        </div>
        <div className="card" style={{padding:0,overflow:"hidden",display:"flex",flexDirection:"column"}}>
          <div style={{padding:"10px 14px",borderBottom:"1px solid var(--mv-border)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:13,fontWeight:600}}>Anteprima (dati di esempio)</span>
            <div className="flex gap-1">
              <button onClick={()=>setViewMobile(false)} className={`btn btn-sm ${!viewMobile?"btn-primary":"btn-ghost"}`}><Monitor size={14}/></button>
              <button onClick={()=>setViewMobile(true)} className={`btn btn-sm ${viewMobile?"btn-primary":"btn-ghost"}`}><Smartphone size={14}/></button>
            </div>
          </div>
          <div style={{flex:1,minHeight:0,overflow:"auto",background:"#f5f3f0",display:"flex",justifyContent:"center",padding:14}}>
            <iframe srcDoc={previewHtml} title="Preview"
              style={{width: viewMobile?360:"100%",maxWidth:viewMobile?360:640,height:"100%",border:"1px solid var(--mv-border)",borderRadius:8,background:"white"}}
              data-testid="template-preview-iframe"/>
          </div>
        </div>
      </div>

      {showVars && (
        <div className="modal-backdrop" onClick={()=>setShowVars(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="h-section mb-4">Variabili disponibili</div>
            <div className="text-small text-muted mb-4">Click per inserire alla fine del template. Usa <code>{`{{NOME}}`}</code> nel codice.</div>
            <div style={{maxHeight:400,overflow:"auto"}}>
              {VARS.map(v => (
                <div key={v.key} className="flex justify-between items-center" style={{padding:"8px 0",borderBottom:"1px solid var(--mv-border)"}}>
                  <div>
                    <code style={{background:"var(--mv-bg)",padding:"2px 6px",borderRadius:4,fontWeight:600}}>{`{{${v.key}}}`}</code>
                    <div className="text-small text-muted mt-2">{v.desc}</div>
                  </div>
                  <button onClick={()=>insertVar(v.key)} className="btn btn-secondary btn-sm">Inserisci</button>
                </div>
              ))}
            </div>
            <button onClick={()=>setShowVars(false)} className="btn btn-primary w-full mt-4">Chiudi</button>
          </div>
        </div>
      )}
    </div>
  );
}
